import React, { useState, useEffect } from 'react';
import moment from 'moment';
import queryString from 'query-string';
import { withRouter } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { setPortal } from 'modules/portal';

import FormControlLabel from '@material-ui/core/FormControlLabel';
import { userOnLogin } from 'lib/api/user';

import { makeRowColumn, getHeadObj, dataConverter } from 'lib/commonLib';
import { unpaidStatistics } from 'lib/api/report';

import MbCheckbox from 'components/atoms/MbCheckbox';
import NewTableContainer from 'components/molecules/NewTable';
import NewFilterWrap from 'components/molecules/NewFilterWrap';
import NewButton from 'components/molecules/NewButton';
import NewCommonFilter from 'components/organisms/NewCommonFilter';

const Unpaid = ({ location }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { portal } = useSelector(({ portal }) => ({
    portal: portal,
  }));

  const convertKeys = [
    'advrtsPrdtCode',
    'advrtsTpCode',
    'pltfomTpCode',
    'itlTpCode',
    'sDate',
    'eDate',
    'uri',
  ];

  let initParams = {
    sort: 'desc',
    order: 'STATS_DTTM',
    sDate: moment().add(-1, 'day').format('YYYYMMDD'),
    eDate: moment().add(-1, 'day').format('YYYYMMDD'),
    balance: true,
    contact: 'all',
  };

  const newInitParams = dataConverter(
    queryString.parse(location.search),
    convertKeys
  );
  initParams = {
    ...initParams,
    ...newInitParams,
  };

  const [initialization, setInitialization] = useState(false);
  const [params, setParams] = useState(initParams);

  /**
   * 필터 초기화 후 값을 바꿈
   */
  useEffect(() => {
    if (initialization === true) {
      setInitialization(false);
    } // eslint-disable-next-line
  }, [initialization]);

  /**
   * 페이지 파라미터를 관리하는 params을 수정하는 함수
   * @param newParams
   */
  const getParam = (newParams) => {
    if (JSON.stringify(params) !== JSON.stringify(newParams)) {
      setParams(newParams);
    }
  };

  // header 및 body 컬럼 커스텀
  const customized = {
    BDGT_LIMIT: {
      makeHead: (headsMap) => {
        return {
          content: (
            <>
              {getHeadObj(headsMap, 'BDGT_LIMIT').label}
              <br />
              (Vat미포함)
            </>
          ),
        };
      },
      makeBody: (rowObj, headsMap) => {
        const bodyItem1 = getHeadObj(headsMap, 'BDGT_LIMIT');

        return (
          <>
            <span title={rowObj['BDGT_LIMIT']}>
              {makeRowColumn(
                rowObj['BDGT_LIMIT'],
                bodyItem1.type,
                bodyItem1.format
              )}
            </span>
          </>
        );
      },
    },
    CTGR_NM: {
      orderFlag: true,
      makeBody: (rowObj, headsMap) => {
        const bodyItem1 = getHeadObj(headsMap, 'CTGR_NM');

        return makeRowColumn(
          rowObj['CTGR_NM'],
          bodyItem1.type,
          bodyItem1.format
        );
      },
    },
    TOT_UN_ADVRTS_AMT: {
      orderFlag: true,
      makeBody: (rowObj, headsMap) => {
        const bodyItem1 = getHeadObj(headsMap, 'TOT_UN_ADVRTS_AMT');

        return makeRowColumn(
          rowObj['TOT_UN_ADVRTS_AMT'],
          bodyItem1.type,
          bodyItem1.format
        );
      },
    },
    RE_UN_ADVRTS_AMT: {
      orderFlag: true,
      makeBody: (rowObj, headsMap) => {
        const bodyItem1 = getHeadObj(headsMap, 'RE_UN_ADVRTS_AMT');

        return makeRowColumn(
          rowObj['RE_UN_ADVRTS_AMT'],
          bodyItem1.type,
          bodyItem1.format
        );
      },
    },
    USER_UN_ADVRTS_AMT: {
      orderFlag: true,
      makeBody: (rowObj, headsMap) => {
        const bodyItem1 = getHeadObj(headsMap, 'USER_UN_ADVRTS_AMT');

        return makeRowColumn(
          rowObj['USER_UN_ADVRTS_AMT'],
          bodyItem1.type,
          bodyItem1.format
        );
      },
    },
  };

  /**
   * 필터 초기화
   */
  const handleOnReset = () => {
    setInitialization(true);
    setParams(initParams);
  };

  const handleBalanceChange = () => {
    setParams({
      ...params,
      balance: !params.balance,
    });
  };

  const addColumnList = (() => {
    const result = new Map();
    result.set(0, {
      id: 'MANAGE_ICONS',
      label: t('관리'),
      align: 'center',
      makeRowFunc: (data) => {
        return (
          data['CONTACT_NAME'] !== 'total' && (
            <NewButton
              title={t('광고주 로그인')}
              icon="faSignInAlt"
              iconBtn
              onClick={(e) => handleOnLogin(data['ADVER_ID'])}
              disableRipple
            />
          )
        );
      },
    });
    return result;
  })();

  /**
   * 로그인처리
   */
  const handleOnLogin = (ADVER_ID) => {
    userOnLogin(ADVER_ID, 'manage')
      .then((response) => {
        const { data, success } = response.data;
        if (success) {
          handlePortalUrl(data.url);
        } else {
          alert('로그인에 실패했습니다. 관리자에게 문의하세요.');
        }
      })
      .catch((error) => {
        console.log('error', error);
      });
  };

  const handlePortalUrl = (url) => {
    let newPortal = portal.portal;
    let popIndex = newPortal.length;
    newPortal[popIndex] = { url: url, cnt: popIndex };
    dispatch(setPortal(newPortal));
  };

  const toolTipText = (
    <p>
      기본노출 데이터 (베너, 아이커버)
      <br />
      * 리타겟팅 : CW + SR * 유저타겟팅 : UM + AD
      <br />※ 한도금액 (부가세 미포함)
    </p>
  );

  return (
    <>
      <NewFilterWrap handleOnReset={handleOnReset}>
        <NewCommonFilter
          initialization={initialization}
          params={params}
          getParam={getParam}
          usePltfomTpCodeFilter={false}
          useExternalFilter={false}
          useManagerFilter
        />
        <FormControlLabel
          control={
            <MbCheckbox
              name="balance"
              onChange={handleBalanceChange}
              value={params.balance}
              checked={params.balance}
              color="#4ed1bd"
              style={{ marginLeft: 10 }}
            />
          }
          label="잔여금액 1,000원 미만 제외"
        />
      </NewFilterWrap>
      <NewTableContainer
        dataReqPromise={unpaidStatistics}
        params={{ ...params, balance: params.balance ? 'Y' : 'N' }}
        getParam={getParam}
        customized={customized}
        addColumnList={addColumnList}
        toolTip={toolTipText}
        useFetch={false}
      />
    </>
  );
};

export default withRouter(Unpaid);
